import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { onCharge, onChargeText, ON_CHARGE_YES } from '../onCharge';

function formatDate(d) {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return '--';
    return new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMoney(v) {
    if (!v && v !== 0) return '--';
    return '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PrintPage() {
    const { pk } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [includeAttachments, setIncludeAttachments] = useState(true);

    useEffect(() => {
        api.getWorkOrder(pk).then(setData).finally(() => setLoading(false));
    }, [pk]);

    if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
    if (!data) return null;

    const wo = data.workOrder;
    const vendor = data.assignments?.find(a => a.isLead)?.laborName || data.assignments?.[0]?.laborName || '';
    const hasAttachments = (data.documents?.length > 0) || (data.images?.length > 0);
    const charge = onCharge(wo.onCharge);
    // A printed page tends to get stapled to the invoice, so a Yes is called out
    // above the table as well — that is the whole point of the field for AP.
    const chargeCell = charge.answer === ON_CHARGE_YES
        ? <strong style={{ color: '#1b5e20' }}>{onChargeText(wo.onCharge)}</strong>
        : onChargeText(wo.onCharge);

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            <div className="no-print" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => window.print()} className="btn btn-primary">Print</button>
                <button onClick={() => window.history.back()} className="btn btn-outline">Back</button>
                {hasAttachments && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginLeft: 16 }}>
                        <input type="checkbox" checked={includeAttachments}
                            onChange={e => setIncludeAttachments(e.target.checked)} />
                        Include attachments ({(data.documents?.length || 0) + (data.images?.length || 0)})
                    </label>
                )}
            </div>

            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Work Order: {wo.id}</h1>
            <div style={{ color: '#666', marginBottom: 16 }}>Status: {wo.statusDesc} | Site: {wo.shopId} {wo.shopName} | Dept: {wo.departmentName}</div>

            {charge.answer === ON_CHARGE_YES && (
                <div style={{ marginBottom: 16, padding: '6px 10px', border: '2px solid #1b5e20', color: '#1b5e20', fontWeight: 700 }}>
                    Cost To Be On Charged: {onChargeText(wo.onCharge)}
                </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, boxShadow: 'none' }}>
                <tbody>
                    {[
                        ['Status', wo.statusDesc, 'Type', wo.typeDesc],
                        ['Priority', wo.priorityDesc, 'Category', wo.categoryName],
                        ['Site', `${wo.shopId} ${wo.shopName}`, 'Department', wo.departmentName],
                        ['GL Account', wo.accountId ? `${wo.accountId} ${wo.accountName}` : '--', 'Repair Center', wo.repairCenter],
                        ['Requester', wo.requesterName, 'Taken By', wo.takenBy],
                        ['Supervisor', wo.supervisorName, 'Auth Status', wo.authStatusDesc],
                        ['Vendor', vendor, 'On Charge', chargeCell],
                        ['Requested', formatDate(wo.requestedDate), 'Target Date', formatDate(wo.targetDate)],
                        ['Completed', formatDate(wo.completeDate), 'Closed', formatDate(wo.closedDate)],
                        ['Total Actual', formatMoney(wo.costTotalActual), 'Total Estimated', formatMoney(wo.costTotalEstimated)],
                    ].map((row, i) => (
                        <tr key={i}>
                            <td style={{ padding: '4px 8px', fontWeight: 600, width: '15%', borderBottom: '1px solid #eee' }}>{row[0]}</td>
                            <td style={{ padding: '4px 8px', width: '35%', borderBottom: '1px solid #eee' }}>{row[1] || '--'}</td>
                            <td style={{ padding: '4px 8px', fontWeight: 600, width: '15%', borderBottom: '1px solid #eee' }}>{row[2]}</td>
                            <td style={{ padding: '4px 8px', width: '35%', borderBottom: '1px solid #eee' }}>{row[2] ? (row[3] || '--') : ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {wo.reason && (
                <div style={{ marginBottom: 12 }}>
                    <strong>Description:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, padding: 8, background: '#f8f8f8', border: '1px solid #eee' }}>{wo.reason}</div>
                </div>
            )}

            {wo.instructions && (
                <div style={{ marginBottom: 12 }}>
                    <strong>Instructions:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, padding: 8, background: '#f8f8f8', border: '1px solid #eee' }}>{wo.instructions}</div>
                </div>
            )}

            {data.statusHistory?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <strong>Status History:</strong>
                    <table style={{ marginTop: 4, boxShadow: 'none' }}>
                        <thead><tr><th>Date</th><th>Status</th><th>By</th></tr></thead>
                        <tbody>
                            {data.statusHistory.map((h, i) => (
                                <tr key={i}><td>{formatDateTime(h.date)}</td><td>{h.description || h.status}</td><td>{h.userName || h.initials}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data.miscCosts?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <strong>Costs:</strong>
                    <table style={{ marginTop: 4, boxShadow: 'none' }}>
                        <thead><tr><th>Item</th><th>Company</th><th>Invoice</th><th>Qty</th><th style={{ textAlign: 'right' }}>Estimated</th><th style={{ textAlign: 'right' }}>Actual</th><th style={{ textAlign: 'right' }}>Charge</th></tr></thead>
                        <tbody>
                            {data.miscCosts.map((m, i) => (
                                <tr key={i}><td>{m.name}</td><td>{m.companyName}</td><td>{m.invoiceNumber}</td><td>{m.quantityActual || ''}</td><td style={{ textAlign: 'right' }}>{formatMoney(m.estimatedCost)}</td><td style={{ textAlign: 'right' }}>{formatMoney(m.actualCost)}</td><td style={{ textAlign: 'right' }}>{formatMoney(m.totalCharge)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {includeAttachments && ((data.documents?.length > 0) || (data.images?.length > 0)) && (
                <>
                    {(data.documents || []).map((d, i) => {
                        if (!d.url) return null;
                        if (/\.(jpe?g|png|gif|webp)$/i.test(d.url)) {
                            return <div key={`d${i}`} style={{ pageBreakBefore: 'always', padding: '24px 0' }}>
                                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Attachment {i + 1}: {d.name}</div>
                                <img src={d.url} alt={d.name} style={{ maxWidth: '100%', maxHeight: '90vh' }}
                                    onError={e => { e.target.parentElement.style.display = 'none'; }} />
                            </div>;
                        }
                        if (/\.pdf$/i.test(d.url)) {
                            return <div key={`d${i}`} style={{ pageBreakBefore: 'always', padding: '24px 0' }}>
                                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Attachment {i + 1}: {d.name}</div>
                                <iframe src={d.url} title={d.name} style={{ width: '100%', height: '90vh', border: '1px solid #ddd' }} />
                            </div>;
                        }
                        return null;
                    })}
                    {(data.images || []).map((img, i) => img.url ? (
                        <div key={`p${i}`} style={{ pageBreakBefore: 'always', padding: '24px 0' }}>
                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Photo: {img.photo || 'Photo'} {img.date ? formatDate(img.date) : ''}</div>
                            <img src={img.url} alt={img.photo} style={{ maxWidth: '100%', maxHeight: '90vh' }}
                                onError={e => { e.target.parentElement.style.display = 'none'; }} />
                        </div>
                    ) : null)}
                </>
            )}

            <div style={{ marginTop: 24, fontSize: 11, color: '#999', borderTop: '1px solid #eee', paddingTop: 8 }}>
                Printed from MC Work Orders -- {new Date().toLocaleString('en-AU')}
            </div>
        </div>
    );
}
